import currencyFormatter from '~/utils/currencyFormatter';

import { ParityAnalysis, RevenueHooks } from './types';

const SEVERITY_BADGE: Record<string, { bg: string; color: string }> = {
  in_parity: { bg: '#d5f5e3', color: '#1e8449' },
  slight: { bg: '#fcf3cf', color: '#7d6608' },
  moderate: { bg: '#fdebd0', color: '#b7600a' },
  severe: { bg: '#fadbd8', color: '#922b21' },
};

const RATING_STYLE: Record<string, { bg: string }> = {
  GOOD: { bg: '#27ae60' },
  FAIR: { bg: '#f39c12' },
  POOR: { bg: '#e74c3c' },
  CRITICAL: { bg: '#922b21' },
};

function fmt(n: number | null): string {
  if (n == null) return '-';
  return currencyFormatter('AUD', n, 2);
}

function badge(severity: string): string {
  const s = SEVERITY_BADGE[severity] ?? { bg: '#eee', color: '#333' };
  return `<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;text-transform:uppercase;background:${
    s.bg
  };color:${s.color}">${severity.replace('_', ' ')}</span>`;
}

export async function exportToPdf(analysis: ParityAnalysis, revenueHooks: RevenueHooks | null) {
  const a = analysis;
  const ratingStyle = RATING_STYLE[a.parityRating] ?? { bg: '#999' };

  const revenueSection = revenueHooks
    ? `
    <div style="border-left:4px solid #e74c3c;padding:24px;margin-bottom:24px;background:#fff;border-radius:0 10px 10px 0">
      <h3 style="margin:0 0 16px;font-size:16px;color:#1B2A4A">Revenue Impact — What Parity Is Costing You</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div style="background:linear-gradient(135deg,#fef9f9,#fff5f5);border-radius:10px;padding:24px">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#922b21">Estimated Revenue You're Missing</div>
          <div style="font-size:36px;font-weight:700;color:#c0392b;margin-top:8px">$${revenueHooks.estimatedExtraRevenueMonthly}</div>
          <div style="font-size:14px;color:#922b21;margin-top:4px">per month (conservative estimate)</div>
          <div style="font-size:13px;color:#95a5a6;margin-top:8px">$${revenueHooks.estimatedExtraRevenueAnnual} per year</div>
        </div>
        <div style="background:#f8f9fb;border-radius:10px;padding:24px">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#7f8c8d;margin-bottom:12px">Your Conversion Rate</div>
          <div style="margin-bottom:12px"><span style="font-size:28px;font-weight:700;color:#e74c3c">${revenueHooks.cvrOutOfParity}%</span> <span style="font-size:14px;color:#95a5a6">when overpriced</span></div>
          <div><span style="font-size:28px;font-weight:700;color:#27ae60">${revenueHooks.cvrAtParity}%</span> <span style="font-size:14px;color:#95a5a6">platform average at parity</span></div>
          <div style="font-size:13px;color:#7f8c8d;margin-top:12px">Fixing parity could increase bookings by ~${revenueHooks.cvrImprovementPct}%</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:16px">
        <div style="text-align:center;padding:12px;border:1px solid #eee;border-radius:8px">
          <div style="font-size:11px;color:#95a5a6;text-transform:uppercase">Search Impressions</div>
          <div style="font-size:20px;font-weight:700;margin:4px 0">${revenueHooks.totalImpressions}</div>
          <div style="font-size:11px;color:#95a5a6">last 30 days</div>
        </div>
        <div style="text-align:center;padding:12px;border:1px solid #eee;border-radius:8px">
          <div style="font-size:11px;color:#95a5a6;text-transform:uppercase">Shown Overpriced</div>
          <div style="font-size:20px;font-weight:700;color:#e74c3c;margin:4px 0">${revenueHooks.outOfParityPct}%</div>
          <div style="font-size:11px;color:#95a5a6">${revenueHooks.impressionsOutOfParity} impressions</div>
        </div>
        <div style="text-align:center;padding:12px;border:1px solid #eee;border-radius:8px">
          <div style="font-size:11px;color:#95a5a6;text-transform:uppercase">Current Orders</div>
          <div style="font-size:20px;font-weight:700;margin:4px 0">${revenueHooks.actualOrders30d}</div>
          <div style="font-size:11px;color:#95a5a6">last 30 days</div>
        </div>
        <div style="text-align:center;padding:12px;border:1px solid #eee;border-radius:8px">
          <div style="font-size:11px;color:#95a5a6;text-transform:uppercase">Current Revenue</div>
          <div style="font-size:20px;font-weight:700;margin:4px 0">$${revenueHooks.actualRevenue30d}</div>
          <div style="font-size:11px;color:#95a5a6">last 30 days</div>
        </div>
      </div>
    </div>
  `
    : '';

  const dateRangesHtml =
    a.dateRanges.length > 0
      ? `
    <div style="background:#fff;border-radius:10px;padding:24px;margin-bottom:24px;box-shadow:0 2px 8px rgba(0,0,0,0.05)">
      <h3 style="font-size:16px;font-weight:600;color:#1B2A4A;margin:0 0 16px;padding-bottom:8px;border-bottom:2px solid #eef2f7">Problem Date Ranges</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <thead>
          <tr>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Date Range</th>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Days</th>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Avg LE Price</th>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Avg Competitor</th>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Gap</th>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Competitor</th>
            <th style="background:#1B2A4A;color:white;padding:10px 12px;text-align:left;font-weight:500;font-size:12px;text-transform:uppercase">Severity</th>
          </tr>
        </thead>
        <tbody>
          ${a.dateRanges
            .map(
              (r) => `
            <tr>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${r.startDate} — ${r.endDate}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${r.numDays}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${fmt(r.avgLePrice)}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${fmt(r.avgCompetitorPrice)}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${r.avgDiffPct}%</td>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${r.competitor}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #eef2f7">${badge(r.severity)}</td>
            </tr>
          `,
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `
      : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Price Parity Report — ${a.propertyName}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; background:#f5f7fa; color:#2c3e50; line-height:1.6; }
    @media print {
      body { background:#fff; }
      .no-print { display:none !important; }
      .page-break { page-break-before:always; }
    }
  </style>
</head>
<body>

<div style="background:linear-gradient(135deg,#1B2A4A 0%,#2c3e50 100%);color:white;padding:40px">
  <h1 style="font-size:28px;font-weight:300;margin-bottom:4px">Price Parity Report</h1>
  <h2 style="font-size:18px;font-weight:400;opacity:0.85">${a.propertyName}</h2>
  <div style="margin-top:12px;font-size:13px;opacity:0.7">Generated ${a.analysisDate} &bull; ${a.dateRangeStart} to ${
    a.dateRangeEnd
  } &bull; ${a.totalCalendarDays} days analyzed</div>
</div>

<div style="max-width:1100px;margin:0 auto;padding:24px">

  <!-- Score Banner -->
  <div style="display:flex;align-items:center;gap:24px;background:white;border-radius:12px;padding:24px 32px;margin:-30px 0 24px;box-shadow:0 4px 16px rgba(0,0,0,0.08);position:relative;z-index:1">
    <div style="width:80px;height:80px;border-radius:50%;background:${
      ratingStyle.bg
    };display:flex;align-items:center;justify-content:center;color:white;font-size:24px;font-weight:700;flex-shrink:0">${
    a.parityScore
  }%</div>
    <div>
      <div style="font-size:14px;color:#7f8c8d">Parity Score</div>
      <div style="font-size:20px;font-weight:600">${a.parityRating}</div>
      <div style="font-size:13px;color:#95a5a6;margin-top:2px">${a.inParityCount} of ${
    a.totalDates
  } comparable dates at parity — ${a.outOfParityCount} dates need attention</div>
    </div>
  </div>

  <!-- Metric Cards -->
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:24px">
    <div style="background:white;border-radius:10px;padding:20px;box-shadow:0 2px 8px rgba(0,0,0,0.05);text-align:center">
      <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.5px;color:#95a5a6;margin-bottom:4px">Avg Price Gap</div>
      <div style="font-size:24px;font-weight:700;color:#1B2A4A">${fmt(a.avgPriceDiff)}</div>
      <div style="font-size:13px;color:#7f8c8d;margin-top:2px">${a.avgPriceDiffPct}% above competitors</div>
    </div>
    <div style="background:white;border-radius:10px;padding:20px;box-shadow:0 2px 8px rgba(0,0,0,0.05);text-align:center">
      <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.5px;color:#95a5a6;margin-bottom:4px">Worst Competitor</div>
      <div style="font-size:18px;font-weight:700;color:#1B2A4A">${a.worstCompetitor}</div>
      <div style="font-size:13px;color:#7f8c8d;margin-top:2px">Cheapest on ${
        a.competitorCheapestCounts[a.worstCompetitor] ?? 0
      } dates</div>
    </div>
    <div style="background:white;border-radius:10px;padding:20px;box-shadow:0 2px 8px rgba(0,0,0,0.05);text-align:center">
      <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.5px;color:#95a5a6;margin-bottom:4px">Severe Issues</div>
      <div style="font-size:24px;font-weight:700;color:#e74c3c">${a.severityCounts.severe ?? 0}</div>
      <div style="font-size:13px;color:#7f8c8d;margin-top:2px">Dates with &gt;10% gap</div>
    </div>
    <div style="background:white;border-radius:10px;padding:20px;box-shadow:0 2px 8px rgba(0,0,0,0.05);text-align:center">
      <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.5px;color:#95a5a6;margin-bottom:4px">Out of Parity</div>
      <div style="font-size:24px;font-weight:700;color:#1B2A4A">${a.outOfParityCount}</div>
      <div style="font-size:13px;color:#7f8c8d;margin-top:2px">of ${a.totalDates} dates</div>
    </div>
  </div>

  <!-- Revenue Impact -->
  ${revenueSection}

  <!-- Date Ranges -->
  ${dateRangesHtml}

</div>

<div style="text-align:center;padding:32px;color:#bdc3c7;font-size:12px">
  Price Parity Report &bull; Generated by Luxury Escapes Connectivity Tools &bull; ${a.analysisDate}
</div>

</body>
</html>`;

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '1100px';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const { default: html2canvas } = await import('html2canvas');
    const { default: jsPDF } = await import('jspdf');

    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/jpeg', 0.9);

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    const pageHeight = 297;

    let yOffset = 0;
    while (yOffset < imgHeight) {
      if (yOffset > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, -yOffset, pageWidth, imgHeight);
      yOffset += pageHeight;
    }

    const safeName = a.propertyName.replace(/[^a-zA-Z0-9]/g, '_');
    pdf.save(`parity_report_${safeName}_${a.analysisDate}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
